import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useUsers } from "@/entities/users/hooks";
import {
  useFilteredTasks,
  useRefreshFilteredTasks,
  useUpdateTaskMutation
} from "@/entities/tasks/hooks";
import { TaskState, TaskTabs } from "@/entities/tasks/types";
import { Filters } from "@/services/api/types";
import { SortingState } from "@tanstack/react-table";
import SuccessToast from "@/components/controls/Task/SuccessToast";

const TRANSLATION_KEY = "focus.tasks";

export interface TaskAnimationState {
  [taskId: string]: {
    localIsCompleted: boolean;
    showStrikethrough: boolean;
    isAnimating: boolean;
    shouldHide: boolean;
  };
}

export const getDefaultSortField = (selectedTab: string) =>
  selectedTab === TaskState.OPEN ? "dueDate" : "completedAt";

interface Args {
  selectedLayoutTab: string;
  selectedTab: string;
  allTasksFilters: Filters;
  assignedToMeFilters: Filters;
  assignedByMeFilters: Filters;
  searchTerm: string;
  sorting: SortingState;
  // Lets a host page that owns more than one useTasksListData call (e.g.
  // one per view) suspend the fetch/users subscription for the instance
  // backing the inactive view, instead of running both at once.
  enabled?: boolean;
}

// Shared data-fetching/mutation logic behind both the Tasks table and cards
// views, so switching between them doesn't duplicate the network fetch or
// the "mark complete" animation/undo behavior.
export const useTasksListData = ({
  selectedLayoutTab,
  selectedTab,
  allTasksFilters,
  assignedToMeFilters,
  assignedByMeFilters,
  searchTerm,
  sorting,
  enabled = true
}: Args) => {
  const { t } = useTranslation();
  const refreshTasks = useRefreshFilteredTasks();
  const updateTask = useUpdateTaskMutation();
  const updateTaskRef = useRef(updateTask);
  updateTaskRef.current = updateTask;

  const [taskAnimationStates, setTaskAnimationStates] =
    useState<TaskAnimationState>({});

  const animationTimersRef = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});
  const hideTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {}
  );

  useEffect(
    () => () => {
      Object.values(animationTimersRef.current).forEach(clearTimeout);
      Object.values(hideTimersRef.current).forEach(clearTimeout);
      animationTimersRef.current = {};
      hideTimersRef.current = {};
    },
    []
  );

  const sortParams = useMemo(() => {
    const field = sorting[0]?.id ?? getDefaultSortField(selectedTab);
    return {
      field,
      asc: !sorting[0]?.desc
    };
  }, [sorting, selectedTab]);

  const currentFilters = useMemo(() => {
    if (selectedLayoutTab === TaskTabs.ALL_TASKS) {
      return allTasksFilters;
    } else if (selectedLayoutTab === TaskTabs.ASSIGNED_TO_ME) {
      return assignedToMeFilters;
    } else {
      return assignedByMeFilters;
    }
  }, [
    selectedLayoutTab,
    allTasksFilters,
    assignedToMeFilters,
    assignedByMeFilters
  ]);

  const {
    entities: tasks,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    isError
  } = useFilteredTasks(
    currentFilters,
    searchTerm ? { term: searchTerm } : undefined,
    undefined,
    undefined,
    sortParams.field,
    sortParams.asc,
    undefined,
    enabled
  );

  const userIds = useMemo(() => {
    const ids = new Set<string>();
    tasks.forEach((task) => {
      if (task.assigneeId) ids.add(task.assigneeId);
      if (task.createdBy) ids.add(task.createdBy);
      if (task.completedBy) ids.add(task.completedBy);
    });
    return Array.from(ids);
  }, [tasks]);
  const { users } = useUsers(userIds, { enabled });

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage && !isError) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, isError, fetchNextPage]);

  const clearAnimationState = useCallback((taskId: string) => {
    setTaskAnimationStates((prev) => {
      const newState = { ...prev };
      delete newState[taskId];
      return newState;
    });
  }, []);

  // Reopening is instant — mirrors the legacy widget's Task.tsx, which
  // only animates the completing direction, not reopening.
  const markReopened = useCallback((taskId: string) => {
    setTaskAnimationStates((prev) => ({
      ...prev,
      [taskId]: {
        localIsCompleted: false,
        showStrikethrough: false,
        isAnimating: false,
        shouldHide: false
      }
    }));
  }, []);

  // A row's local state outlives the mutation and is dropped only once the
  // fetched list no longer has the row or agrees with it, so a completed task
  // never flashes back from the pre-refetch data.
  useEffect(() => {
    setTaskAnimationStates((prev) => {
      const fetchedIsCompleted = new Map(
        tasks.map((task) => [task.id, task.isCompleted])
      );
      const settledIds = Object.keys(prev).filter(
        (taskId) =>
          !fetchedIsCompleted.has(taskId) ||
          fetchedIsCompleted.get(taskId) === prev[taskId].localIsCompleted
      );
      if (settledIds.length === 0) return prev;
      const next = { ...prev };
      settledIds.forEach((taskId) => delete next[taskId]);
      return next;
    });
  }, [tasks]);

  const handleUpdateIsCompleted = useCallback(
    (taskId: string, isCompleted: boolean) => {
      updateTaskRef.current.mutate(
        {
          pathParams: { taskId },
          data: { isCompleted }
        },
        {
          onSuccess: () => {
            refreshTasks();

            if (isCompleted) {
              toast.success(
                <SuccessToast
                  undoLabel={t("undo")}
                  successTitle={t(`${TRANSLATION_KEY}.taskCompleted`)}
                  handleUndo={() => {
                    markReopened(taskId);
                    handleUpdateIsCompleted(taskId, false);
                  }}
                  refreshTasks={refreshTasks}
                />,
                { duration: 5000 }
              );
            }
          },
          onError: () => {
            clearAnimationState(taskId);
            toast.error(t(`${TRANSLATION_KEY}.taskUpdateFailed`));
          }
        }
      );
    },
    [clearAnimationState, markReopened, refreshTasks, t]
  );

  const handleCheckedChange = useCallback(
    (taskId: string, checked: boolean) => {
      clearTimeout(animationTimersRef.current[taskId]);
      clearTimeout(hideTimersRef.current[taskId]);
      delete animationTimersRef.current[taskId];
      delete hideTimersRef.current[taskId];

      if (!checked) {
        markReopened(taskId);
        handleUpdateIsCompleted(taskId, false);
        return;
      }

      setTaskAnimationStates((prev) => ({
        ...prev,
        [taskId]: {
          localIsCompleted: true,
          showStrikethrough: true,
          isAnimating: false,
          shouldHide: false
        }
      }));

      animationTimersRef.current[taskId] = setTimeout(() => {
        delete animationTimersRef.current[taskId];
        setTaskAnimationStates((prev) => ({
          ...prev,
          [taskId]: { ...prev[taskId], isAnimating: true }
        }));

        hideTimersRef.current[taskId] = setTimeout(() => {
          delete hideTimersRef.current[taskId];
          setTaskAnimationStates((prev) => ({
            ...prev,
            [taskId]: { ...prev[taskId], shouldHide: true }
          }));
          handleUpdateIsCompleted(taskId, true);
        }, 1000);
      }, 1000);
    },
    [handleUpdateIsCompleted, markReopened]
  );

  const visibleTasks = useMemo(
    () => tasks.filter((task) => !taskAnimationStates[task.id]?.shouldHide),
    [tasks, taskAnimationStates]
  );

  return {
    tasks: visibleTasks,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
    users,
    taskAnimationStates,
    handleLoadMore,
    handleCheckedChange
  };
};
